import { useRef } from "react";
import type { MouseEvent } from "react";

/**
 * Backdrop-click handler that ignores drag-out events.
 *
 * The naive `onClick={onClose}` on a modal backdrop fires whenever the
 * mouseup lands on the backdrop, even when the mousedown started inside the
 * modal. That happens any time a user selects text in an input or textarea
 * and releases the mouse past the modal edge — the click event's target
 * resolves to the nearest common ancestor of the mousedown and mouseup
 * elements, which is the backdrop, and the modal closes mid-selection.
 *
 * This hook fixes it by requiring BOTH the mousedown AND the click to have
 * originated on the backdrop element itself.
 */
export function useModalBackdropClose(onClose: () => void) {
  const downOnBackdropRef = useRef(false);

  const onMouseDown = (e: MouseEvent<HTMLElement>) => {
    downOnBackdropRef.current = e.target === e.currentTarget;
  };

  const onClick = (e: MouseEvent<HTMLElement>) => {
    if (downOnBackdropRef.current && e.target === e.currentTarget) {
      onClose();
    }
    downOnBackdropRef.current = false;
  };

  return { onMouseDown, onClick };
}
